// @flow

import * as React from 'react'
import styled, { css } from 'styled-components'

import { WidgetContainer } from '../shared'
import {
  LAYOUT_1X2,
  LAYOUT_1X3,
  LAYOUT_1X4,
  LAYOUT_2X2,
  LAYOUT_2PLUS1,
  LAYOUT_1PLUS2,
  LAYOUT_NUMBER_OF_COLUMNS,
  LAYOUT_NUMBER_OF_ROWS,
  LAYOUT_PREVIEW_NUMBER_OF_CELLS,
  LAYOUT_MASONRY,
  LAYOUT_CUSTOM,
} from './shared'

const WebshopGrid = ({ gridItem, editedGridItemId, ...rest }) => {
  const isGridEdited = editedGridItemId && gridItem.id === editedGridItemId

  const previewNumberOfCells =
    gridItem.data.layout === LAYOUT_CUSTOM
      ? gridItem.data.custom_number_of_columns *
        gridItem.data.custom_number_of_rows
      : LAYOUT_PREVIEW_NUMBER_OF_CELLS[gridItem.data.layout]

  let numberOfPreviewItems = 0
  if (isGridEdited && gridItem.children.length < previewNumberOfCells) {
    numberOfPreviewItems = previewNumberOfCells - gridItem.children.length
  }

  return (
    <WidgetContainer gridItem={gridItem}>
      <GridContainer
        gridTemplateColumns={buildGridTemplateColumns(gridItem)}
        gridTemplateRows={buildGridTemplateRows(gridItem)}
        columnGap={`${gridItem.data.column_gap}px`}
        rowGap={`${gridItem.data.row_gap}px`}
        stackOnMobile={gridItem.data.stack_on_mobile}
      >
        {gridItem.children.map((childGridItem, childGridItemIndex) => {
          const GridItemComponent = childGridItem.component

          if (!GridItemComponent) {
            return null
          }

          return (
            <GridItemContainer
              key={childGridItemIndex}
              gridArea={buildGridAreaForChild(gridItem, childGridItemIndex)}
              stackOnMobile={gridItem.data.stack_on_mobile}
              isGridEdited={isGridEdited}
            >
              <GridItemComponent
                gridItem={childGridItem}
                editedGridItemId={editedGridItemId}
                {...rest}
              />
              {isGridEdited && (
                <GridItemPreviewOverlay>
                  Grid cell #{childGridItemIndex + 1}
                </GridItemPreviewOverlay>
              )}
            </GridItemContainer>
          )
        })}

        {numberOfPreviewItems > 0 &&
          Array.from({ length: numberOfPreviewItems }).map((_, index) => (
            <GridItemContainer
              key={index}
              gridArea={buildGridAreaForChild(
                gridItem,
                gridItem.children.length + index
              )}
              stackOnMobile={gridItem.data.stack_on_mobile}
              isGridEdited={isGridEdited}
            >
              <GridItemPreviewOverlay>
                Grid cell #{gridItem.children.length + index + 1}
              </GridItemPreviewOverlay>
            </GridItemContainer>
          ))}
      </GridContainer>
    </WidgetContainer>
  )
}

export default WebshopGrid

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: ${props => props.gridTemplateColumns};
  grid-template-rows: ${props => props.gridTemplateRows};
  column-gap: ${props => props.columnGap};
  row-gap: ${props => props.rowGap};

  ${props =>
    props.stackOnMobile &&
    css`
      @media (max-width: 420px) {
        grid-template-columns: 1fr;
        grid-template-rows: auto;
      }
    `}
`

const GridItemContainer = styled.div`
  grid-area: ${props => props.gridArea};
  overflow: hidden;

  ${props =>
    props.stackOnMobile &&
    css`
      @media (max-width: 420px) {
        grid-area: auto;
      }
    `}

  ${props =>
    props.isGridEdited &&
    css`
      position: relative;
      min-height: 40px;
    `}
`

const GridItemPreviewOverlay = styled.div`
  position: absolute;
  inset: 0;

  background-color: rgba(213, 213, 213, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  color: #777777;
  font-size: 14px;
  line-height: 1;
`

const buildGridTemplateColumns = gridItem => {
  const numberOfColumns =
    gridItem.data.layout === LAYOUT_CUSTOM
      ? gridItem.data.custom_number_of_columns
      : LAYOUT_NUMBER_OF_COLUMNS[gridItem.data.layout]

  const gridTemplateColumnsParts = []

  for (let i = 0; i < numberOfColumns; i++) {
    const columnWidthConfig = gridItem.data.column_widths[i]

    if (columnWidthConfig && columnWidthConfig.mode === 'dynamic') {
      gridTemplateColumnsParts.push(`${columnWidthConfig.dynamic_value}fr`)
    } else if (columnWidthConfig && columnWidthConfig.mode === 'fixed') {
      gridTemplateColumnsParts.push(`${columnWidthConfig.fixed_value}px`)
    } else {
      gridTemplateColumnsParts.push('1fr')
    }
  }

  return gridTemplateColumnsParts.join(' ')
}

const buildGridTemplateRows = gridItem => {
  const numberOfRows =
    gridItem.data.layout === LAYOUT_CUSTOM
      ? gridItem.data.custom_number_of_rows
      : LAYOUT_NUMBER_OF_ROWS[gridItem.data.layout]

  const gridTemplateRowsParts = []

  for (let i = 0; i < numberOfRows; i++) {
    const rowHeightConfig = gridItem.data.row_heights[i]

    if (rowHeightConfig && rowHeightConfig.mode === 'fixed') {
      gridTemplateRowsParts.push(`${rowHeightConfig.fixed_value}px`)
    } else {
      gridTemplateRowsParts.push('auto')
    }
  }

  return gridTemplateRowsParts.join(' ')
}

const buildGridAreaForChild = (gridItem, childIndex: number) => {
  if (gridItem.data.layout === LAYOUT_1X2 && childIndex <= 1) {
    return `1 / ${childIndex + 1} / 1 / ${childIndex + 1}`
  }

  if (gridItem.data.layout === LAYOUT_1X3 && childIndex <= 2) {
    return `1 / ${childIndex + 1} / 1 / ${childIndex + 1}`
  }

  if (gridItem.data.layout === LAYOUT_1X4 && childIndex <= 3) {
    return `1 / ${childIndex + 1} / 1 / ${childIndex + 1}`
  }

  if (gridItem.data.layout === LAYOUT_2X2 && childIndex <= 3) {
    const columnNum = (childIndex % 2) + 1
    const rowNum = Math.floor(childIndex / 2) + 1

    return `${rowNum} / ${columnNum} / ${rowNum} / ${columnNum}`
  }

  if (gridItem.data.layout === LAYOUT_1PLUS2 && childIndex <= 2) {
    return {
      '0': '1 / 1 / span 2 / 1',
      '1': '1 / 2 / 1 / 2',
      '2': '2 / 2 / 2 / 2',
    }[String(childIndex)]
  }

  if (gridItem.data.layout === LAYOUT_2PLUS1 && childIndex <= 2) {
    return {
      '0': '1 / 1 / 1 / 1',
      '1': '1 / 2 / span 2 / 2',
      '2': '2 / 1 / 2 / 1',
    }[String(childIndex)]
  }

  if (gridItem.data.layout === LAYOUT_MASONRY && childIndex <= 3) {
    return {
      '0': '1 / 1 / 1 / span 2',
      '1': '1 / 3 / 1 / span 1',
      '2': '2 / 1 / 2 / span 1',
      '3': '2 / 2 / 2 / span 2',
    }[String(childIndex)]
  }

  if (gridItem.data.layout === LAYOUT_CUSTOM) {
    // TODO: implement custom layout items
    return 'auto'
  }

  // For unrecognized layouts or children, which are over the threshold for
  // given layout we just use auto
  return 'auto'
}
